import React from "react";
import { useTranslation } from "react-i18next";
import { SCHEDULE_TOGGLE_FIELD, actionIsSwitchedOn, hasScheduleToggle } from "./actionSetup";
import { routeDetailsClasses } from "../tailwindClassnames";
import SwitchVisual, { switchTrackFill } from "../SwitchVisual";

type ScheduleToggleProps = {
    action: any;
    // The section's current form data — the switch reads `run_on_schedule` from
    // it, so it always agrees with what would be saved.
    data: any;
    // Receives the whole configuration with the flag flipped, plus the field id,
    // so hosts treat it exactly like any other user edit to the form.
    onChange: (data: any, fieldId: string) => void;
    disabled?: boolean;
};

// An action's on/off switch, sitting on the same line as the section's name.
// Off means the connector never runs this action, so the section's fields are
// read-only and its required fields don't hold up a save.
const ScheduleToggle = ({ action, data, onChange, disabled = false }: ScheduleToggleProps) => {
    const { t } = useTranslation();
    if (!hasScheduleToggle(action)) return null;
    const on = actionIsSwitchedOn(action, data);

    return (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            // Named for what it switches; the state is aria-checked's job.
            aria-label={action.ui_schema?.["ui:title"] || action.name || t("Enabled")}
            disabled={disabled}
            data-testid={`ScheduleToggle-${action.value || action.id}-switch`}
            onClick={() => onChange({ ...(data ?? {}), [SCHEDULE_TOGGLE_FIELD]: !on }, SCHEDULE_TOGGLE_FIELD)}
            className={`flex items-center gap-2 select-none ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
            {/* Inside the button: a <label> can't label a button, so a word beside
                one is inert while looking like part of the control. */}
            {/* The portal's one switch, drawn by the same code as the rjsf boolean
                widget (SwitchVisual): same size, same fills, same X and checkmark.
                This control used to roll its own — smaller, a different grey, and
                no icons — so two toggles on one screen looked like two different
                kinds of control. */}
            <span className={`${routeDetailsClasses.switchContainer} ${switchTrackFill(on)}`}>
                <SwitchVisual on={on} />
            </span>
            {/* Switch first, then its state as a word, the way a switch reads
                left to right. */}
            <span className={`text-sm ${on ? "text-ui-text-primary-text" : "text-greyscale-5"}`}>
                {on ? t("On") : t("Off")}
            </span>
        </button>
    );
};

export default ScheduleToggle;
